import {Component} from 'panel';

import {registerMPElement} from 'common/util/register-element';
import {createElem} from 'common/util/dom';
import {apiClient} from 'common/util/api-client';

import template from './index.jade';
import css from './index.styl?inline';

import './support-widget-container';

interface HelpMenuSection {
  label: string;
  icon: string;
  href?: string;
  target?: string;
  disabled?: boolean;
  handleItemClick?: () => void;
}

interface BillingAccountResults {
  id: number;
  recurring_service_arr: number;
  arr: number;
}

interface UserSupportInfoResults {
  billing_account: BillingAccountResults;
  service_plan_ids: number[];
  livechat_enabled: boolean;
}

interface BillingAccountData {
  billing_account: BillingAccountResults;
  service_plan_ids: number[];
}

interface HelpWidgetState {
  billingAccountData: BillingAccountData;
  isOpen: boolean;
  isSupportWidgetMaximized: boolean;
  livechatEnabled: boolean;
  showSupportWidget: boolean;
  showTutorial: boolean;
}

class HelpWidget extends Component<HelpWidgetState> {
  static get attrsSchema() {
    return {
      hidden: `boolean`,
      'livechat-enabled': `boolean`,
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        billingAccountData: null,
        isOpen: false,
        isSupportWidgetMaximized: false,
        livechatEnabled: false,
        showSupportWidget: false,
        showTutorial: false,
      },

      helpers: {
        helpMenuSections: (): HelpMenuSection[] =>
          [
            {
              label: `Make a suggestion`,
              icon: `idea`,
              href: `https://community.mixpanel.com?&utm_campaign=help-widget&utm_content=suggestion-widget&utm_medium=help-widget&utm_source=in-product`,
              target: `_blank`,
            },
            {
              label: `Help center`,
              icon: `help-center`,
              href: `https://help.mixpanel.com?utm_source=in-product&utm_campaign=help-widget`,
              target: `_blank`,
            },
            {
              label: `Community`,
              icon: `community`,
              href: `https://community.mixpanel.com?&utm_campaign=help-widget&utm_content=community-widget&utm_medium=help-widget&utm_source=in-product`,
              target: `_blank`,
            },
            {
              label: `Contact support`,
              icon: `email`,
              handleItemClick: () => {
                this.update({showSupportWidget: true});
              },
            },
            {
              label: `Live chat`,
              icon: `sms`,
              disabled: !this.state.livechatEnabled,
              handleItemClick: () => {
                this.helpers.activateZendeskChat();
              },
            },
          ].filter((item) => !item.disabled),

        toggleHelpMenu: () => {
          this.update({isOpen: !this.state.isOpen});
        },

        closeSupportWidget: () => {
          this.update({showSupportWidget: false});
        },

        onMenuClose: (ev) => {
          if (ev.detail.state === `closed`) {
            this.update({isOpen: false});
          }
        },

        gracefulJsonParse: (str) => {
          try {
            return JSON.parse(str);
          } catch {
            return null;
          }
        },

        activateZendeskChat: () => {
          mixpanel.people.increment(`[Help Widget] Live Chat Started`, 1);
          this.helpers.embedZendeskScript();

          window[`zE`](function() {
            // @ts-ignore
            const zdWidget: any = zE;
            zdWidget.hide();
            zdWidget.activate();
            zdWidget.identify({
              name: mp.report.globals.user_name,
              email: mp.report.globals.user_email,
            });
          });
        },

        embedZendeskScript: () => {
          // see: https://developer.zendesk.com/embeddables/docs/widget/gtm
          const w: any = window;
          const d: any = document;
          d.zendeskHost = `mixpanelsupport.zendesk.com`;

          if (!w.zEmbed) {
            const queue = [];
            w.zEmbed = function() {
              queue.push(arguments); // eslint-disable-line prefer-rest-params
            };
            w.zE = w.zE || w.zEmbed;
            d.zEQueue = queue;
          }

          const scriptSrc = `https://assets.zendesk.com/embeddable_framework/main.js`;
          if (!document.head.querySelector(`script[src='${scriptSrc}']`)) {
            document.head.appendChild(
              createElem(`script`, {
                src: scriptSrc,
                'data-ze-csp': `true`,
                async: true,
                defer: true,
              }),
            );
          }
        },

        showMaximizedSupportWidget: () => {
          this.update({
            isSupportWidgetMaximized: true,
            showSupportWidget: true,
          });
        },
      },
    };
  }

  async connectedCallback() {
    super.connectedCallback();

    const params = new URLSearchParams(window.location.search);
    if (params.has(`contact-us`) && params.get(`contact-us`) !== `false`) {
      // Waiting for DOMContentLoaded to show the maximized support widget since it depends on
      // mp.report.globals (in help-widget/index.jade), which is not guaranteed to exist when
      // the help widget is inserted in the DOM
      window.addEventListener(`DOMContentLoaded`, this.helpers.showMaximizedSupportWidget);
    }

    // zendesk stores it's state in localStorage as ZD-store.
    // if we see that the widget was previously shown,
    // we'll re-activate zendeskChat so user has persistent chat experience.
    // NOTE: gdpr users will still have to start a new session since gdpr disables localStorage
    // and zendesk widget won't remember it's state.
    const zdStore = this.helpers.gracefulJsonParse(localStorage.getItem(`ZD-store`));
    if (zdStore && zdStore.widgetShown) {
      this.helpers.activateZendeskChat();
    }

    const supportInfo = await apiClient.get<UserSupportInfoResults>(`/api/app/billing/user_support_info`);
    const {billing_account, livechat_enabled, service_plan_ids} = supportInfo;
    this.update({
      billingAccountData: {billing_account, service_plan_ids},
      livechatEnabled: livechat_enabled,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(`DOMContentLoaded`, this.helpers.showMaximizedSupportWidget);
  }
}

registerMPElement(`mp-help-widget`, HelpWidget);
